import { useCallback, useEffect, useRef, useState } from "react";

/** What a note tells the hook when someone starts dragging it. */
export interface NoteDragStart {
  noteId: string;
  columnId: string;
  /** The note's box when the drag began, in viewport coordinates. */
  rect: { left: number; top: number; width: number; height: number };
  clientX: number;
  clientY: number;
}

/** Where a dragged note ended up. Coordinates are relative to the canvas. */
export interface NoteDrop {
  noteId: string;
  columnId: string;
  x: number;
  y: number;
}

export interface NoteDrag {
  noteId: string;
  /** The column the note started in. */
  columnId: string;
  width: number;
  height: number;
  /** Viewport position of the note's top-left corner right now. */
  left: number;
  top: number;
  /** The canvas under the pointer, if the pointer is over one. */
  overColumnId: string | null;
  /** The note the pointer has rested on long enough to group into. */
  groupTargetId: string | null;
  /** False until the pointer has actually travelled — a click is not a drag. */
  moved: boolean;
}

interface Options {
  onDrop: (drop: NoteDrop) => void;
  onGroup: (noteId: string, parentId: string) => void;
  /** How long the pointer must rest on another note before it groups. */
  dwellMs?: number;
}

/** Under this many pixels of travel, the gesture was a click. */
const DRAG_THRESHOLD = 4;

interface Hit {
  columnId: string;
  rect: DOMRect;
}

function contains(rect: DOMRect, x: number, y: number): boolean {
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

/**
 * The canvas a note released at this point belongs to.
 *
 * A canvas fills its column card, but the column has a header above it, and a
 * release over that header is plainly meant for that column — so anywhere on
 * the column counts, and the point is clamped onto its canvas.
 */
function canvasUnder(x: number, y: number): Hit | null {
  const canvases = Array.from(
    document.querySelectorAll<HTMLElement>("[data-column-canvas]")
  );

  for (const canvas of canvases) {
    const rect = canvas.getBoundingClientRect();
    if (contains(rect, x, y)) {
      return { columnId: canvas.dataset.columnCanvas!, rect };
    }
  }

  const regions = Array.from(
    document.querySelectorAll<HTMLElement>("[data-column-region]")
  );

  for (const region of regions) {
    if (!contains(region.getBoundingClientRect(), x, y)) continue;

    const canvas = region.querySelector<HTMLElement>("[data-column-canvas]");
    if (!canvas) continue;

    return {
      columnId: canvas.dataset.columnCanvas!,
      rect: canvas.getBoundingClientRect(),
    };
  }

  return null;
}

/**
 * The topmost note under the pointer, ignoring the one being dragged.
 *
 * Notes render back-to-front, so the last match in document order is the one
 * visibly on top — which is the one a person aiming at a pile means.
 */
function noteUnder(x: number, y: number, exceptId: string): string | null {
  const notes = Array.from(
    document.querySelectorAll<HTMLElement>("[data-note-root]")
  );

  let topmost: string | null = null;
  for (const note of notes) {
    const id = note.dataset.noteRoot;
    if (!id || id === exceptId) continue;

    const rect = note.getBoundingClientRect();
    if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
      topmost = id;
    }
  }

  return topmost;
}

/**
 * Dragging post-its around the board with the pointer.
 *
 * Not HTML5 drag-and-drop: notes are placed to the pixel now, and a drag image
 * that trails the cursor while the note itself stays put makes it impossible
 * to see where the note will actually land. Pointer events also cover touch,
 * which native dragging does not.
 *
 * Dropping a note *onto* another is ambiguous once notes may overlap, so it
 * only groups when the pointer rests on the target (or Alt is held) — a quick
 * drop just leaves the note lying on top.
 */
export function useNoteDrag({ onDrop, onGroup, dwellMs = 600 }: Options) {
  const [drag, setDrag] = useState<NoteDrag | null>(null);

  // The listeners are attached once per drag and must see the live values, so
  // the drag is mirrored into a ref rather than closed over from state.
  const dragRef = useRef<NoteDrag | null>(null);
  const grabRef = useRef({ x: 0, y: 0, startX: 0, startY: 0 });
  const hoverRef = useRef<string | null>(null);
  const dwellRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const update = useCallback((patch: Partial<NoteDrag>) => {
    setDrag((current) => {
      if (!current) return current;
      const next = { ...current, ...patch };
      dragRef.current = next;
      return next;
    });
  }, []);

  const clearDwell = useCallback(() => {
    if (dwellRef.current) clearTimeout(dwellRef.current);
    dwellRef.current = null;
    hoverRef.current = null;
  }, []);

  const stop = useCallback(() => {
    clearDwell();
    document.body.style.userSelect = "";
    dragRef.current = null;
    setDrag(null);
  }, [clearDwell]);

  const start = useCallback((origin: NoteDragStart) => {
    const next: NoteDrag = {
      noteId: origin.noteId,
      columnId: origin.columnId,
      width: origin.rect.width,
      height: origin.rect.height,
      left: origin.rect.left,
      top: origin.rect.top,
      overColumnId: null,
      groupTargetId: null,
      moved: false,
    };

    grabRef.current = {
      x: origin.clientX - origin.rect.left,
      y: origin.clientY - origin.rect.top,
      startX: origin.clientX,
      startY: origin.clientY,
    };

    // A press that lands on a note lands on its words, so without this the
    // drag sweeps a text selection across the board along with the note. Set
    // here rather than in an effect: the selection would already have started
    // by the time a render came round.
    document.body.style.userSelect = "none";
    window.getSelection()?.removeAllRanges();

    dragRef.current = next;
    setDrag(next);
  }, []);

  const active = drag !== null;

  useEffect(() => {
    if (!active) return;

    const move = (event: PointerEvent) => {
      const current = dragRef.current;
      if (!current) return;

      const grab = grabRef.current;
      const travelled =
        Math.abs(event.clientX - grab.startX) +
        Math.abs(event.clientY - grab.startY);

      const hit = canvasUnder(event.clientX, event.clientY);
      const hovered = noteUnder(event.clientX, event.clientY, current.noteId);

      // Alt is the impatient version of resting on the target.
      if (hovered && event.altKey) {
        clearDwell();
        hoverRef.current = hovered;
        update({ groupTargetId: hovered });
      } else if (hovered !== hoverRef.current) {
        clearDwell();
        hoverRef.current = hovered;
        if (current.groupTargetId) update({ groupTargetId: null });
        if (hovered) {
          dwellRef.current = setTimeout(
            () => update({ groupTargetId: hovered }),
            dwellMs
          );
        }
      }

      update({
        left: event.clientX - grab.x,
        top: event.clientY - grab.y,
        overColumnId: hit?.columnId ?? null,
        moved: current.moved || travelled > DRAG_THRESHOLD,
      });
    };

    const finish = (event: PointerEvent) => {
      const current = dragRef.current;
      stop();

      // A press that never travelled is a click on the note, not a move.
      if (!current || !current.moved) return;

      if (current.groupTargetId) {
        onGroup(current.noteId, current.groupTargetId);
        return;
      }

      const hit = canvasUnder(event.clientX, event.clientY);
      // Released over the page rather than a column: leave the note alone.
      if (!hit) return;

      const grab = grabRef.current;
      const left = event.clientX - grab.x - hit.rect.left;
      const top = event.clientY - grab.y - hit.rect.top;

      onDrop({
        noteId: current.noteId,
        columnId: hit.columnId,
        x: Math.max(0, Math.min(left, hit.rect.width - current.width)),
        y: Math.max(0, top),
      });
    };

    const cancel = (event: KeyboardEvent) => {
      if (event.key === "Escape") stop();
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", stop);
    window.addEventListener("keydown", cancel);

    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", stop);
      window.removeEventListener("keydown", cancel);
    };
  }, [active, clearDwell, dwellMs, onDrop, onGroup, stop, update]);

  useEffect(
    () => () => {
      clearDwell();
      document.body.style.userSelect = "";
    },
    [clearDwell]
  );

  return { drag, startDrag: start };
}
